// Accessibility: skip link, and a settings sheet (text size, high contrast, reduce motion, readable font,
// underlined links, strong focus rings) saved on this device and applied as classes on <html>.
import React, { useEffect, useState } from "react";
import { Accessibility, X } from "lucide-react";
import { useDraggableFab } from "./useDraggableFab";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const KEY = "fable.a11y.v1";
const SIZES = [100, 112, 125, 150];
const TOGGLES = [
  { key: "contrast", label: "High contrast", hint: "Stronger text and borders, no faint greys" },
  { key: "motion", label: "Reduce motion", hint: "Stops animations, parallax and auto-playing effects" },
  { key: "font", label: "Readable font", hint: "Atkinson Hyperlegible, designed for low vision" },
  { key: "links", label: "Underline links", hint: "Makes every link easy to spot" },
  { key: "focus", label: "Strong focus outline", hint: "A thick ring around whatever the keyboard is on" },
  { key: "spacing", label: "More spacing", hint: "Extra line and letter spacing for easier reading" },
];
const DEFAULTS = { size: 100, contrast: false, motion: false, font: false, links: false, focus: false, spacing: false };

function load() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    if (v) return { ...DEFAULTS, ...v };
  } catch (e) { /* storage blocked */ }
  let motion = false;
  try { motion = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { /* old browser */ }
  return { ...DEFAULTS, motion };
}

function apply(s) {
  const el = document.documentElement;
  el.style.fontSize = s.size === 100 ? "" : s.size + "%";
  TOGGLES.forEach((t) => el.classList.toggle("a11y-" + t.key, !!s[t.key]));
  if (s.font && !document.getElementById("a11y-font")) {
    const l = document.createElement("link");
    l.id = "a11y-font"; l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap";
    document.head.appendChild(l);
  }
}

export function SkipLink() {
  const skip = (e) => {
    e.preventDefault();
    const m = document.querySelector("main") || document.getElementById("root");
    if (!m) return;
    if (!m.hasAttribute("tabindex")) m.setAttribute("tabindex", "-1");
    m.focus();
    m.scrollIntoView();
  };
  return <a href="#main" onClick={skip} className="a11y-skip" data-testid="skip-link">Skip to main content</a>;
}

export function AccessibilityPanel() {
  const [s, setS] = useState(load);
  const [open, setOpen] = useState(false);
  useEffect(() => { apply(s); try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* storage blocked */ } }, [s]);
  useEffect(() => {
    const o = () => setOpen(true);
    window.addEventListener("fable:a11y", o);
    return () => window.removeEventListener("fable:a11y", o);
  }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const fab = useDraggableFab();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Accessibility settings. Drag to move it, or press Shift and an arrow key." title="Accessibility settings (drag to move)" data-testid="a11y-button" {...fab.handlers} style={fab.style}
        className="fixed bottom-4 left-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-neutral-900 text-white shadow-lg ring-1 ring-white/20 transition hover:scale-105 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-amber-400 dark:bg-white dark:text-neutral-900">
        <Accessibility className="h-5 w-5" aria-hidden="true" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[340px] overflow-y-auto sm:w-[380px]" data-testid="a11y-panel">
          <SheetHeader>
            <SheetTitle>Accessibility</SheetTitle>
            <SheetDescription>Saved on this device. Press Ctrl / ⌘ + K any time to jump anywhere.</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <p className="text-sm font-semibold">Text size</p>
            <div className="mt-2 grid grid-cols-4 gap-2" role="radiogroup" aria-label="Text size">
              {SIZES.map((z) => (
                <button key={z} type="button" role="radio" aria-checked={s.size === z} onClick={() => set("size", z)} data-testid={"a11y-size-" + z}
                  className={"h-11 rounded-xl border text-sm font-semibold transition " + (s.size === z ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900" : "border-neutral-300 dark:border-neutral-700")}>
                  {z}%
                </button>
              ))}
            </div>
          </div>
          <ul className="mt-6 space-y-1">
            {TOGGLES.map((t) => (
              <li key={t.key}>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl p-3 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  <input type="checkbox" checked={!!s[t.key]} onChange={(e) => set(t.key, e.target.checked)} className="mt-1 h-5 w-5 accent-neutral-900" data-testid={"a11y-" + t.key} />
                  <span>
                    <span className="block text-sm font-semibold">{t.label}</span>
                    <span className="block text-xs text-neutral-600 dark:text-neutral-400">{t.hint}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={() => setS({ ...DEFAULTS })} className="h-11 rounded-full border px-5 text-sm font-semibold" data-testid="a11y-reset">Reset</button>
            <a href="/accessibility" className="text-sm font-semibold underline">Accessibility statement</a>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="sr-only">Close <X /></button>
          {fab.moved && <button type="button" onClick={fab.reset} className="mt-6 text-sm font-semibold underline" data-testid="a11y-reset-position">Put the accessibility button back in the corner</button>}
        </SheetContent>
      </Sheet>
    </>
  );
}
